import { ChevronDown } from 'lucide-react';
import { Fragment, useCallback, useId, useMemo, useState } from 'react';

import {
    GroupSelectionWarningDialog,
    useGroupSelectionWarning,
} from '@apps/web/components/outreaches/group_selection_warning';
import { InhouseRecommendationsTable } from '@apps/web/components/outreaches/inhouse_recommendations_table';
import {
    type OutreachScheduleConflict,
    OutreachScheduleConflictDialog,
    parseOutreachScheduleConflict,
} from '@apps/web/components/outreaches/outreach_schedule_conflict_dialog';
import { useSkipListRetry } from '@apps/web/components/outreaches/outreach_skip_list_dialog';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    buildCaregiverShiftIdsFromSelections,
    type CaregiverShiftIds,
    normalizeSelectedCaregiverIds,
} from '@apps/web/lib/caregiver_shift_selections';
import {
    formatDateAndTimeWithWeekday,
    formatTime,
} from '@apps/web/lib/date_utils';
import { parseKnownApiError } from '@apps/web/lib/known_api_errors';
import {
    buildOutreachDisplayUnits,
    type OutreachDisplayUnit,
} from '@apps/web/lib/outreach_shift_ordering';
import { createStartOutreachTimeout } from '@apps/web/lib/outreach_started_report';
import { cn } from '@apps/web/lib/utils';
import {
    useOrganization,
    useOrganizationDataSourceAccounts,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    ApiError,
    type JoinedOutreachRead,
    type Shift,
} from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

type AddCaregiversModalProps = {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly outreach: JoinedOutreachRead;
    readonly contactedCaregiverIds: Array<string>;
    readonly onSuccess: () => void;
};
type CaregiverNamesById = Readonly<Record<string, string>>;
type AddCaregiverDisplayUnit = OutreachDisplayUnit<Shift>;
type SuggestedCaregiver = NonNullable<
    JoinedOutreachRead['suggested_caregivers']
>[number];
type AccordionHeaderTimeRow = {
    readonly label: string;
    readonly selectedCount: number;
    readonly shiftId: string;
};

function getPendingShifts(outreach: JoinedOutreachRead): Array<Shift> {
    const metadata = outreach.outreach_metadata as {
        readonly filled_shifts?: Record<string, unknown>;
        readonly cancelled_shifts?: Array<string>;
    } | null;
    const filledShiftIds = new Set(Object.keys(metadata?.filled_shifts ?? {}));
    const cancelledShiftIds = new Set(metadata?.cancelled_shifts ?? []);
    const now = new Date();
    return outreach.shifts.filter(
        (s) =>
            !filledShiftIds.has(s.id) &&
            !cancelledShiftIds.has(s.id) &&
            new Date(s.shift_end_time) > now,
    );
}

function getSuggestedCaregiverIdsForUnit({
    shiftIds,
    suggestions,
}: {
    readonly shiftIds: ReadonlyArray<string>;
    readonly suggestions: ReadonlyArray<SuggestedCaregiver>;
}): Array<string> {
    const unitShiftIds = new Set(shiftIds);
    const seenCaregiverIds = new Set<string>();
    const suggestedCaregiverIds: Array<string> = [];

    for (const suggestion of suggestions) {
        if (
            seenCaregiverIds.has(suggestion.caregiver_id) ||
            !suggestion.covered_shift_ids.some((shiftId) =>
                unitShiftIds.has(shiftId),
            )
        ) {
            continue;
        }

        seenCaregiverIds.add(suggestion.caregiver_id);
        suggestedCaregiverIds.push(suggestion.caregiver_id);
    }

    return suggestedCaregiverIds;
}

export function AddCaregiversModal({
    isOpen,
    onClose,
    outreach,
    contactedCaregiverIds,
    onSuccess,
}: AddCaregiversModalProps) {
    const { formatName } = useFormatPersonName();
    const organizationId = useOrganizationId();
    const { timezone } = useOrganizationTimezone();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'add-caregivers-modal',
    });
    const sortByLastName =
        organization?.features?.last_name_sorting_enabled === true;
    const { data: dataSourceAccounts } = useOrganizationDataSourceAccounts(
        organizationId,
        { reason: 'add-caregivers-modal' },
    );
    const isAxisCare =
        dataSourceAccounts?.some((account) => account.type === 'axiscare') ??
        false;

    const pendingShifts = useMemo(() => getPendingShifts(outreach), [outreach]);

    const displayUnits = useMemo(
        () => buildOutreachDisplayUnits(pendingShifts, outreach.shift_groups),
        [pendingShifts, outreach.shift_groups],
    );
    const suggestedCaregiverIdsByUnitId = useMemo(() => {
        const idsByUnitId = new Map<string, Array<string>>();
        for (const unit of displayUnits) {
            idsByUnitId.set(
                unit.id,
                getSuggestedCaregiverIdsForUnit({
                    shiftIds: unit.shiftIds,
                    suggestions: outreach.suggested_caregivers ?? [],
                }),
            );
        }
        return idsByUnitId;
    }, [displayUnits, outreach.suggested_caregivers]);

    const accordionId = useId();
    const [openUnitIds, setOpenUnitIds] = useState<Set<string>>(
        () => new Set(),
    );

    const [selectedCaregiverIdsByShift, setSelectedCaregiverIdsByShift] =
        useState<Record<string, Array<string>>>({});
    const [useSameCaregiversAcrossShifts, setUseSameCaregiversAcrossShifts] =
        useState(false);
    // Once parent state has synchronized the tables, keep supplying each
    // shift's copied selection so turning sync off starts from that snapshot.
    const [hasEverSyncedAcrossShifts, setHasEverSyncedAcrossShifts] =
        useState(false);
    const [selectedCaregiverNameById, setSelectedCaregiverNameById] = useState<
        Record<string, string>
    >({});
    const [isAddingSelectedCaregivers, setIsAddingSelectedCaregivers] =
        useState(false);
    const [scheduleConflict, setScheduleConflict] =
        useState<OutreachScheduleConflict | null>(null);
    const [conflictingSubmission, setConflictingSubmission] = useState<{
        readonly caregiverShiftIds: CaregiverShiftIds;
        readonly caregiverNamesById?: Readonly<Record<string, string>>;
    } | null>(null);
    const [isConfirmingScheduleConflict, setIsConfirmingScheduleConflict] =
        useState(false);
    const { handleSkipListRefusal, skipListDialog } = useSkipListRetry<{
        readonly caregiverNamesById?: Readonly<Record<string, string>>;
        readonly acknowledgedConflicts: Record<string, Array<string>>;
    }>({
        setSelectedCaregiverIdsByShift,
        resubmit: (caregiverShiftIds, retry): Promise<boolean> =>
            submitAddCaregivers(
                caregiverShiftIds,
                retry.caregiverNamesById,
                retry.acknowledgedConflicts,
            ),
    });

    const activeShiftGroups = useMemo(() => {
        const pendingShiftIdSet = new Set(pendingShifts.map((s) => s.id));
        return (outreach.shift_groups ?? [])
            .map((g) => ({
                ...g,
                shift_ids: g.shift_ids.filter((id) =>
                    pendingShiftIdSet.has(id),
                ),
            }))
            .filter((g) => g.shift_ids.length > 1);
    }, [outreach.shift_groups, pendingShifts]);

    const shiftLabelById = useMemo(() => {
        const labels = new Map<string, string>();
        for (const shift of outreach.shifts ?? []) {
            labels.set(
                shift.id,
                `${formatDateAndTimeWithWeekday(shift.shift_start_time, timezone)} - ${shift.patient_name}`,
            );
        }
        return labels;
    }, [outreach.shifts, timezone]);

    const { checkForWarnings, warningDialogProps } = useGroupSelectionWarning({
        shifts: pendingShifts,
        shiftGroups: activeShiftGroups,
        shiftLabelById,
        sortByLastName,
    });

    const handleSelectionChangeForUnit = useCallback(
        (
            unit: AddCaregiverDisplayUnit,
            caregiverIds: Array<string>,
            caregiverNamesById: CaregiverNamesById = {},
        ) => {
            const normalized = normalizeSelectedCaregiverIds(caregiverIds);
            if (Object.keys(caregiverNamesById).length > 0) {
                setSelectedCaregiverNameById((prev) => ({
                    ...prev,
                    ...caregiverNamesById,
                }));
            }
            setSelectedCaregiverIdsByShift((prev) => {
                const targetShiftIds = useSameCaregiversAcrossShifts
                    ? pendingShifts.map((shift) => shift.id)
                    : unit.shiftIds;
                const alreadyMatches = targetShiftIds.every((id) => {
                    const existing = prev[id] ?? [];
                    return (
                        existing.length === normalized.length &&
                        existing.every((cid, i) => cid === normalized[i])
                    );
                });
                if (alreadyMatches) {
                    return prev;
                }
                const next = { ...prev };
                for (const id of targetShiftIds) {
                    next[id] = normalized;
                }
                return next;
            });
        },
        [pendingShifts, useSameCaregiversAcrossShifts],
    );

    const handleSameCaregiversAcrossShiftsToggle = useCallback(
        (isChecked: boolean) => {
            setUseSameCaregiversAcrossShifts(isChecked);
            if (!isChecked) {
                return;
            }

            setHasEverSyncedAcrossShifts(true);
            // Preserve every existing pick when independently edited shifts
            // are combined for the first time.
            const sourceCaregiverIds = normalizeSelectedCaregiverIds(
                pendingShifts.flatMap(
                    (shift) => selectedCaregiverIdsByShift[shift.id] ?? [],
                ),
            );
            setSelectedCaregiverIdsByShift(
                Object.fromEntries(
                    pendingShifts.map((shift) => [
                        shift.id,
                        [...sourceCaregiverIds],
                    ]),
                ),
            );
        },
        [pendingShifts, selectedCaregiverIdsByShift],
    );

    const syncedSelectedCaregiverIdsByUnitId = useMemo(() => {
        const idsByUnitId = new Map<string, Array<string>>();
        if (!hasEverSyncedAcrossShifts) {
            return idsByUnitId;
        }
        for (const unit of displayUnits) {
            idsByUnitId.set(
                unit.id,
                normalizeSelectedCaregiverIds(
                    unit.shiftIds.flatMap(
                        (shiftId) => selectedCaregiverIdsByShift[shiftId] ?? [],
                    ),
                ),
            );
        }
        return idsByUnitId;
    }, [displayUnits, hasEverSyncedAcrossShifts, selectedCaregiverIdsByShift]);

    const submitAddCaregivers = useCallback(
        async (
            caregiverShiftIds: CaregiverShiftIds,
            caregiverNamesById?: Readonly<Record<string, string>>,
            acknowledgedConflicts: Record<string, Array<string>> = {},
        ): Promise<boolean> => {
            assert.isNotNull(organizationId);

            if (Object.keys(caregiverShiftIds).length === 0) {
                toast.error('Select at least one caregiver to add.', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                return false;
            }

            let droppedInactiveIds: Array<string> = [];
            let droppedNoValidShiftsIds: Array<string> = [];
            // Same endpoint, same hang: an unbounded POST leaves this modal
            // reading "Adding…" forever with no way out.
            const startTimeout = createStartOutreachTimeout();
            try {
                const response = await getApiClient().post({
                    url: '/organizations/{organization_id}/outreaches/{outreach_id}/start-outreach',
                    pathParams: {
                        organization_id: organizationId,
                        outreach_id: outreach.id,
                    },
                    body: {
                        caregiver_shift_ids: caregiverShiftIds,
                        skip_conflict_check: false,
                        acknowledged_conflicts: acknowledgedConflicts,
                        suppress_agent_notification: false,
                    },
                    reason: 'add-caregiver-modal',
                    signal: startTimeout.signal,
                });
                droppedInactiveIds =
                    response?.dropped_inactive_caregiver_ids ?? [];
                droppedNoValidShiftsIds =
                    response?.dropped_no_valid_shifts_caregiver_ids ?? [];
            } catch (error) {
                const conflict = parseOutreachScheduleConflict(error);
                if (conflict) {
                    setScheduleConflict(conflict);
                    setConflictingSubmission({
                        caregiverShiftIds,
                        caregiverNamesById,
                    });
                    return false;
                }
                if (
                    handleSkipListRefusal(error, caregiverShiftIds, {
                        caregiverNamesById,
                        acknowledgedConflicts,
                    })
                ) {
                    return false;
                }
                const knownError = parseKnownApiError(error);
                if (knownError?.kind === 'all_caregivers_inactive') {
                    toast.error(
                        'All selected caregivers are inactive. Select at least one active caregiver.',
                        {
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        },
                    );
                    return false;
                }
                // The agent started adding people while this modal was open.
                // Surface the server's reason so the user knows to wait.
                if (error instanceof ApiError && error.status === 409) {
                    toast.error(error.message, {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    });
                    return false;
                }
                toast.error('Failed to add caregivers to the open shift', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                return false;
            } finally {
                startTimeout.clear();
            }

            const droppedSet = new Set([
                ...droppedInactiveIds,
                ...droppedNoValidShiftsIds,
            ]);
            const addedCount =
                Object.keys(caregiverShiftIds).length - droppedSet.size;
            const mergedNames: Readonly<Record<string, string>> = {
                ...selectedCaregiverNameById,
                ...(caregiverNamesById ?? {}),
            };
            const buildLabel = (ids: ReadonlyArray<string>): string => {
                const names = ids
                    .map((id) => mergedNames[id])
                    .filter((name): name is string => Boolean(name));
                if (names.length === ids.length && names.length > 0) {
                    return names.join(', ');
                }
                return `${ids.length} caregiver${ids.length === 1 ? '' : 's'}`;
            };
            if (droppedSet.size > 0) {
                const addedPrefix =
                    addedCount > 0
                        ? `Added ${addedCount} caregiver${
                              addedCount === 1 ? '' : 's'
                          } to the open shift; `
                        : '';
                // Only the first emitted toast carries the
                // "Added N caregivers" prefix; the second uses a
                // sentence-start "Skipped …" so the user doesn't read
                // the added count twice.
                const fallbackStart = addedPrefix ? 'skipped' : 'Skipped';
                if (droppedInactiveIds.length > 0) {
                    toast.success(
                        `${addedPrefix}${fallbackStart} ${buildLabel(
                            droppedInactiveIds,
                        )} because they're inactive.`,
                        {
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        },
                    );
                }
                if (droppedNoValidShiftsIds.length > 0) {
                    const prefix =
                        droppedInactiveIds.length > 0 ? '' : addedPrefix;
                    const start =
                        droppedInactiveIds.length > 0
                            ? 'Skipped'
                            : fallbackStart;
                    toast.warning(
                        `${prefix}${start} ${buildLabel(
                            droppedNoValidShiftsIds,
                        )} because the suggested shifts aren’t on this outreach. Re-suggest with valid shifts to include them.`,
                        {
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        },
                    );
                }
            } else {
                toast.success(
                    `Successfully added ${addedCount} caregiver${
                        addedCount === 1 ? '' : 's'
                    } to the open shift`,
                    {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    },
                );
            }
            setScheduleConflict(null);
            setConflictingSubmission(null);
            onSuccess();
            onClose();
            return true;
        },
        [
            organizationId,
            outreach.id,
            onSuccess,
            onClose,
            selectedCaregiverNameById,
            handleSkipListRefusal,
        ],
    );

    const handleAddCaregivers = useCallback(
        async (
            unit: AddCaregiverDisplayUnit,
            caregiverIds: Array<string>,
            caregiverNamesById: CaregiverNamesById,
        ): Promise<boolean> => {
            assert.isNotNull(organizationId);
            const [representativeShift] = unit.shifts;
            if (!representativeShift) {
                return false;
            }
            const mergedNamesById = {
                ...selectedCaregiverNameById,
                ...caregiverNamesById,
            };
            const normalizedCurrentIds =
                normalizeSelectedCaregiverIds(caregiverIds);
            const selectionByShiftForSubmit = {
                ...selectedCaregiverIdsByShift,
            };
            const targetShiftIds = useSameCaregiversAcrossShifts
                ? pendingShifts.map((shift) => shift.id)
                : unit.shiftIds;
            for (const shiftId of targetShiftIds) {
                selectionByShiftForSubmit[shiftId] = normalizedCurrentIds;
            }
            const caregiverShiftIds = buildCaregiverShiftIdsFromSelections({
                selectedCaregiverIdsByShift: selectionByShiftForSubmit,
                shifts: pendingShifts,
                currentShiftId: representativeShift.id,
                currentCaregiverIds: caregiverIds,
            });

            if (
                checkForWarnings({
                    caregiverShiftIds,
                    caregiverNamesById: mergedNamesById,
                    onConfirm: async (confirmedIds) => {
                        await submitAddCaregivers(
                            confirmedIds,
                            mergedNamesById,
                        );
                    },
                })
            ) {
                return false;
            }

            return submitAddCaregivers(caregiverShiftIds, mergedNamesById);
        },
        [
            organizationId,
            selectedCaregiverIdsByShift,
            selectedCaregiverNameById,
            pendingShifts,
            useSameCaregiversAcrossShifts,
            checkForWarnings,
            submitAddCaregivers,
        ],
    );
    const selectedCaregiverShiftIds = useMemo<CaregiverShiftIds>(() => {
        const [firstShift] = pendingShifts;
        if (!firstShift) {
            return {};
        }
        return buildCaregiverShiftIdsFromSelections({
            selectedCaregiverIdsByShift,
            shifts: pendingShifts,
            currentShiftId: firstShift.id,
            currentCaregiverIds:
                selectedCaregiverIdsByShift[firstShift.id] ?? [],
        });
    }, [pendingShifts, selectedCaregiverIdsByShift]);
    const selectedCaregiverCount = Object.keys(
        selectedCaregiverShiftIds,
    ).length;
    const selectedCaregiverShiftCount = new Set(
        Object.values(selectedCaregiverShiftIds).flat(),
    ).size;
    const isAddSelectedDisabled =
        isAddingSelectedCaregivers || selectedCaregiverCount === 0;
    const clearSelectedCaregivers = useCallback(() => {
        setSelectedCaregiverIdsByShift({});
    }, []);
    const handleAddSelectedCaregivers = useCallback(async () => {
        if (isAddSelectedDisabled) {
            return;
        }

        if (
            checkForWarnings({
                caregiverShiftIds: selectedCaregiverShiftIds,
                caregiverNamesById: selectedCaregiverNameById,
                onConfirm: async (confirmedIds) => {
                    await submitAddCaregivers(
                        confirmedIds,
                        selectedCaregiverNameById,
                    );
                },
            })
        ) {
            return;
        }

        setIsAddingSelectedCaregivers(true);
        const didAdd = await submitAddCaregivers(
            selectedCaregiverShiftIds,
            selectedCaregiverNameById,
        );
        if (!didAdd) {
            setIsAddingSelectedCaregivers(false);
        }
    }, [
        checkForWarnings,
        isAddSelectedDisabled,
        selectedCaregiverNameById,
        selectedCaregiverShiftIds,
        submitAddCaregivers,
    ]);

    if (displayUnits.length === 0) {
        return null;
    }

    const toggleUnit = (unitId: string) => {
        setOpenUnitIds((currentOpenUnitIds) => {
            const nextOpenUnitIds = new Set(currentOpenUnitIds);
            if (nextOpenUnitIds.has(unitId)) {
                nextOpenUnitIds.delete(unitId);
            } else {
                nextOpenUnitIds.add(unitId);
            }
            return nextOpenUnitIds;
        });
    };

    const accordionItems = displayUnits.flatMap((unit) => {
        const [firstShift] = unit.shifts;
        if (!firstShift) {
            return [];
        }
        const headerGroups = Array.from(
            unit.shifts.reduce((groups, shift) => {
                const timeRows = groups.get(shift.patient_name) ?? [];
                timeRows.push({
                    shiftId: shift.id,
                    label: `${formatDateAndTimeWithWeekday(
                        shift.shift_start_time,
                        timezone,
                    )} - ${formatTime(shift.shift_end_time, timezone)}`,
                    selectedCount: new Set(
                        selectedCaregiverIdsByShift[shift.id] ?? [],
                    ).size,
                });
                groups.set(shift.patient_name, timeRows);
                return groups;
            }, new Map<string, Array<AccordionHeaderTimeRow>>()),
        ).map(([clientName, timeRows]) => ({
            clientName,
            timeRows,
        }));
        return [
            {
                fullLabel: `${formatDateAndTimeWithWeekday(
                    firstShift.shift_start_time,
                    timezone,
                )} - ${formatName(firstShift.patient_name)}`,
                headerGroups,
                unit,
            },
        ];
    });

    const renderTableForUnit = (unit: AddCaregiverDisplayUnit) => {
        const [representativeShift] = unit.shifts;
        if (!representativeShift) {
            return null;
        }
        const patientTags = (representativeShift.patient_tags ?? []).filter(
            (tag) => tag.trim().length > 0,
        );
        const defaultTagFilters =
            isAxisCare && patientTags.length > 0 ? patientTags : undefined;
        const shiftGroupId = unit.kind === 'group' ? unit.id : undefined;
        const suggestedCaregiverIds =
            suggestedCaregiverIdsByUnitId.get(unit.id) ?? [];
        const syncedSelectedCaregiverIds =
            syncedSelectedCaregiverIdsByUnitId.get(unit.id);

        return (
            <InhouseRecommendationsTable
                key={unit.id}
                outreachAgentGeneration={outreach.agent_generation}
                shiftId={representativeShift.id}
                shiftGroupId={shiftGroupId}
                onStartOutreach={(caregiverIds, caregiverNamesById) =>
                    handleAddCaregivers(unit, caregiverIds, caregiverNamesById)
                }
                onSelectionChange={(caregiverIds, caregiverNamesById) =>
                    handleSelectionChangeForUnit(
                        unit,
                        caregiverIds,
                        caregiverNamesById,
                    )
                }
                showStartButton={false}
                buttonText="Add Selected Caregivers"
                disabledCaregiverIds={contactedCaregiverIds}
                defaultTagFilters={defaultTagFilters}
                outreachId={outreach.id}
                suggestedCaregiverIds={suggestedCaregiverIds}
                initialSelectedCaregiverIds={syncedSelectedCaregiverIds}
            />
        );
    };

    return (
        <Fragment>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent
                    className="relative flex h-[calc(100dvh-4rem)] max-h-[calc(100dvh-4rem)] flex-col overflow-hidden max-sm:h-[90dvh] max-sm:max-h-[90dvh] sm:top-8 sm:bottom-8 sm:max-w-[80vw] sm:translate-y-0"
                    onFocusOutside={(e) => {
                        // Recommendation filter value-pickers
                        // (Popover/DropdownMenu/Select) render with
                        // modal={false} and portal outside DialogContent.
                        // Changing a filter refetches and re-renders the
                        // table, closing the picker and returning focus
                        // outside the dialog, which would otherwise dismiss
                        // the modal. Never dismiss on focus changes; Escape
                        // and backdrop clicks still close it. (PHO-12795)
                        e.preventDefault();
                    }}
                    onPointerDownOutside={(e) => {
                        const originalTarget = e.detail.originalEvent.target;
                        const target =
                            originalTarget instanceof HTMLElement
                                ? originalTarget
                                : null;
                        // (1) Pointer-down inside a filter picker's content
                        // (Popover/DropdownMenu/Select portal outside the
                        // dialog) must not dismiss the modal.
                        if (
                            target?.closest(
                                '[data-radix-popper-content-wrapper]',
                            )
                        ) {
                            e.preventDefault();
                            return;
                        }
                        // (2) The custom numeric filters (contact attempts,
                        // distance, star rating) are Popovers dismissed by an
                        // OUTSIDE click, which lands on the backdrop and so is
                        // not caught by (1). While any picker is open, that
                        // click closes the picker, not the modal. The picker
                        // is still mounted during this synchronous handler, so
                        // keep the modal open when one is present. Tooltip
                        // poppers are excluded so hover-only hints do not
                        // block a genuine backdrop click from closing it.
                        const hasOpenPicker = Array.from(
                            document.querySelectorAll(
                                '[data-radix-popper-content-wrapper]',
                            ),
                        ).some(
                            (popper) =>
                                popper.querySelector('[role="tooltip"]') ===
                                null,
                        );
                        if (hasOpenPicker) {
                            e.preventDefault();
                        }
                    }}
                >
                    <DialogHeader className="flex-row items-start justify-between gap-4 space-y-0">
                        <div className="min-w-0 space-y-1.5">
                            <DialogTitle>Add More Caregivers</DialogTitle>
                            <DialogDescription>
                                Select additional caregivers to contact for this
                                open shift. They will be added to the outreach
                                queue.
                            </DialogDescription>
                        </div>
                        <Button
                            size="sm"
                            disabled={isAddSelectedDisabled}
                            onClick={() => {
                                void handleAddSelectedCaregivers();
                            }}
                        >
                            {isAddingSelectedCaregivers
                                ? 'Adding…'
                                : 'Add Selected Caregivers'}
                        </Button>
                    </DialogHeader>
                    {pendingShifts.length > 1 && (
                        <label className="text-muted-foreground flex w-fit items-center gap-1.5 text-sm">
                            <Checkbox
                                checked={useSameCaregiversAcrossShifts}
                                onCheckedChange={(checked) =>
                                    handleSameCaregiversAcrossShiftsToggle(
                                        checked === true,
                                    )
                                }
                            />
                            Same caregivers for all shifts
                        </label>
                    )}
                    <div
                        className={cn(
                            'min-h-0 flex-1 space-y-2 overflow-y-auto pr-1',
                            selectedCaregiverCount > 0 ? 'pb-24' : '',
                        )}
                        role="group"
                        aria-label="Caregiver recommendation groups and shifts"
                    >
                        {accordionItems.map((item, index) => {
                            const isOpen = openUnitIds.has(item.unit.id);
                            const panelId = `${accordionId}-add-caregiver-unit-${index}`;
                            return (
                                <div
                                    key={item.unit.id}
                                    className="border-border/70 bg-background overflow-hidden rounded-md border"
                                >
                                    <button
                                        type="button"
                                        aria-expanded={isOpen}
                                        aria-controls={panelId}
                                        title={item.fullLabel}
                                        onClick={() => toggleUnit(item.unit.id)}
                                        className="hover:bg-muted/60 flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left transition-colors"
                                    >
                                        <span className="min-w-0 space-y-1">
                                            {item.headerGroups.map(
                                                ({ clientName, timeRows }) => (
                                                    <span
                                                        key={clientName}
                                                        className="block space-y-0.5"
                                                    >
                                                        <span className="text-foreground block text-sm leading-snug font-medium">
                                                            {clientName}
                                                        </span>
                                                        {timeRows.map(
                                                            (timeRow) => (
                                                                <span
                                                                    key={`${clientName}-${timeRow.shiftId}`}
                                                                    className="text-muted-foreground block text-sm leading-snug"
                                                                >
                                                                    {
                                                                        timeRow.label
                                                                    }
                                                                    <span className="text-agent-action ml-1 font-medium dark:text-violet-300">
                                                                        ·{' '}
                                                                        {
                                                                            timeRow.selectedCount
                                                                        }{' '}
                                                                        selected
                                                                    </span>
                                                                </span>
                                                            ),
                                                        )}
                                                    </span>
                                                ),
                                            )}
                                        </span>
                                        <ChevronDown
                                            className={cn(
                                                'text-muted-foreground mt-0.5 size-4 shrink-0 transition-transform',
                                                isOpen ? 'rotate-180' : '',
                                            )}
                                            aria-hidden="true"
                                        />
                                    </button>
                                    <div
                                        id={panelId}
                                        hidden={!isOpen}
                                        className="border-t p-2"
                                    >
                                        <div className="min-h-0">
                                            {renderTableForUnit(item.unit)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {selectedCaregiverCount > 0 && (
                            <div className="pointer-events-none absolute inset-x-0 bottom-4 z-50 flex justify-center px-4">
                                <div className="border-border bg-background text-foreground pointer-events-auto flex w-fit max-w-[calc(100%-2rem)] flex-wrap items-center gap-2 rounded-full border px-3 py-1.5 shadow-md">
                                    <span className="text-xs font-medium">
                                        {selectedCaregiverCount} selected
                                    </span>
                                    {selectedCaregiverShiftCount > 1 && (
                                        <span className="text-muted-foreground text-xs">
                                            across {selectedCaregiverShiftCount}{' '}
                                            shifts
                                        </span>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-foreground hover:bg-foreground/10 h-7 px-2 text-xs"
                                        disabled={isAddingSelectedCaregivers}
                                        onClick={clearSelectedCaregivers}
                                    >
                                        Clear
                                    </Button>
                                    <div className="bg-foreground/20 h-4 w-px" />
                                    <Button
                                        size="sm"
                                        className="text-xs"
                                        disabled={isAddSelectedDisabled}
                                        onClick={() => {
                                            void handleAddSelectedCaregivers();
                                        }}
                                    >
                                        {isAddingSelectedCaregivers
                                            ? 'Adding…'
                                            : 'Add Selected Caregivers'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
            <GroupSelectionWarningDialog
                {...warningDialogProps}
                confirmLabel="Add Selected Caregivers"
            />
            <OutreachScheduleConflictDialog
                conflict={scheduleConflict}
                shifts={outreach.shifts}
                timezone={timezone}
                isSubmitting={isConfirmingScheduleConflict}
                onCancel={() => {
                    setScheduleConflict(null);
                    setConflictingSubmission(null);
                }}
                onConfirm={(acknowledgedConflicts) => {
                    if (!conflictingSubmission) {
                        return;
                    }
                    setIsConfirmingScheduleConflict(true);
                    void submitAddCaregivers(
                        conflictingSubmission.caregiverShiftIds,
                        conflictingSubmission.caregiverNamesById,
                        acknowledgedConflicts,
                    ).finally(() => {
                        setIsConfirmingScheduleConflict(false);
                    });
                }}
            />
            {skipListDialog}
        </Fragment>
    );
}
